import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PLACEMENT_STEPS, type PlacementStepKey, type PlacementsRead } from "./setupSteps";
import { useTRPC } from "./trpc";

/** The fields of a `boxes.list` row that decide whether it can be listed in the store. */
export interface BoxSaleInput {
  status: "draft" | "active" | "archived";
  currentVersion: number;
}

/** A box can go on sale once its rules have built it: active, with a drop table to sell. */
export function canPutOnSale(box: BoxSaleInput): boolean {
  return box.status === "active" && box.currentVersion > 0;
}

/** The part of `status.billing` that decides whether a box may be created. */
export interface BoxCreateBilling {
  status: string;
}

/**
 * What stops "Create a box": a plan not approved, or approved and frozen by Shopify; the
 * placements not read yet (`"pending"`) or not readable (`"failed"`); or the first placement still
 * to do.
 */
export type CreateBoxBlocker = "plan" | "frozen" | "pending" | "failed" | PlacementStepKey;

/**
 * Why a box cannot be created yet, the first cause it hits in the setup guide's order, or null
 * once every step of it is done. The plan counts only while Shopify reports it active, as
 * `boxes.create` requires, so a frozen plan stops it though the guide counts frozen as approved;
 * a raised fee still to approve does not, since the approved plan keeps billing. The placements
 * are the admin's own gate: the server cannot see them, so `boxes.create` checks only the plan.
 */
export function createBoxBlocker(
  billing: BoxCreateBilling | null,
  placements: PlacementsRead,
): CreateBoxBlocker | null {
  if (billing?.status === "frozen") return "frozen";
  if (billing?.status !== "active") return "plan";
  if (typeof placements === "string") return placements;
  return PLACEMENT_STEPS.find((key) => !placements[key]) ?? null;
}

/**
 * Lists a box in the store or takes it off (`boxes.setEnabled`), then reads the box list again.
 * Home and Boxes both switch it, with the same words.
 */
export function useSetBoxOnSale() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.boxes.setEnabled.mutationOptions({
      onSuccess: (result) => {
        shopify.toast.show(
          result.enabled ? "Box is now for sale in your store." : "Box removed from your store.",
        );
        void queryClient.invalidateQueries({ queryKey: trpc.boxes.list.queryKey() });
      },
    }),
  );
}
